import { useState } from "react";
import Table from "../../../components/ui/table";
import { columns, patientAppointmentColumn } from "../utils/data";
import Button from "../../../components/ui/button";
import ActionsList from "./ActionsList";
import { TRescheduleAppointmentDetails } from "../../../types";
import { actions } from "../../../assets";
import moment from "moment";
import { Box, Flex, Text } from "@chakra-ui/react";
import { ClipLoader } from "react-spinners";
import colors from "../../../utils/colors";
import { useNavigate, useParams } from "react-router-dom";

interface IProps {
  setIsCancelModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDetailsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsRescheduleModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCompleteModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;

  appointments: any[];
  loadingAppointments: boolean;
  totalAppointments: number;
  setSelectedAppointment?: React.Dispatch<
    React.SetStateAction<TRescheduleAppointmentDetails>
  >;
  hasActions?: boolean;
  reference?: "doctor" | "patient";
}

const AppointmentTable = ({
  setIsCancelModalOpen,
  setIsDetailsModalOpen,
  setIsRescheduleModalOpen,
  setIsCompleteModalOpen,
  hasActions = true,
  appointments,
  loadingAppointments,
  totalAppointments,
  setSelectedAppointment,
  reference = "doctor",
}: IProps) => {
  const [openAction, setOpenAction] = useState(false);
  const [clickedUserId, setClickedUserId] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  const colsToUse = { doctor: columns, patient: patientAppointmentColumn };

  const rows = appointments?.map((app) => ({
    "Appointment ID": app?._id,
    "Initial Appointment ID": app?.initialAppointmentId,
    Note: app?.notes,
    "Appointment Address": app?.address,
    "Appointment Status": app?.appointmentStatus,
    ...(reference === "doctor" && {
      "Patient Name": app?.patientDetails?.name,
    }),
    ...(reference === "doctor" && {
      "Patient ID": app?.patientDetails?.patientId,
    }),
    ...(reference === "doctor" && {
      Doctor: app?.doctorDetails?.name || app?.doctorName,
    }),
    ...(reference === "patient" && {
      Doctor: app?.doctorDetails?.name || app?.doctorName,
    }),
    Date: moment(app?.date)?.format("DD MMM YYYY"),
    Time: `${moment(app?.startTime)
      ?.subtract(1, "hour")
      ?.format("h:mma")} - ${moment(app?.endTime)
      ?.subtract(1, "hour")
      ?.format("h:mma")}`,
    "Appointment Mode": app?.appointmentType?.mode || "N/A",
    "Appointment Category": app?.appointmentType?.category,
    Actions: actions,
  }));

  const onClickDots = (event: React.MouseEvent, column: string, id: string) => {
    event.stopPropagation();
    if (column === "Actions") {
      setOpenAction(true);
      setClickedUserId(id);
    }
  };

  const displayActions = (column: string, id: string) => {
    if (column === "Actions" && openAction && id === clickedUserId) {
      const appointment = appointments.find((a) => a._id === id);

      return (
        <ActionsList
          appointmentStatus={appointment?.appointmentStatus}
          onClose={setOpenAction}
          openCancelModal={setIsCancelModalOpen}
          openDetailsModal={setIsDetailsModalOpen}
          openRescheduleModal={setIsRescheduleModalOpen}
          openCompleteModal={setIsCompleteModalOpen}
        />
      );
    }
  };

  const renderActionsCell = (icon: string, column: string, row: any) => {
    if (hasActions) {
      return (
        <img
          src={icon}
          alt=""
          onClick={(event) => {
            onClickDots(event, column, row["Appointment ID"]);
            setSelectedAppointment && setSelectedAppointment(row);
          }}
          className="cursor-pointer"
        />
      );
    } else {
      return (
        <Text
          color={colors.primary}
          fontSize={"14px"}
          fontWeight={600}
          cursor="pointer"
          onClick={() => {
            navigate(
              `/patients/${id ?? row["Patient ID"]}/appointments/${
                row["Appointment ID"]
              }/details`
            );
          }}
        >
          View Details
        </Text>
      );
    }
  };

  const renderAppointmentTypeCell = (type: string) => {
    if (type === "Online") {
      return (
        <Button
          text="Online"
          theme="primary"
          size="xs"
          className="!rounded-t-[20px] !rounded-b-[20px] w-[60px] !p-2"
        />
      );
    } else {
      return (
        <Button
          text="Offline"
          theme="secondary"
          size="xs"
          className="!rounded-t-[20px] !rounded-b-[20px] w-[60px] !p-2"
        />
      );
    }
  };

  return (
    <Box>
      {loadingAppointments ? (
        <Flex justifyContent="center" alignItems="center" height="200px">
          <ClipLoader color={colors.primary} />
        </Flex>
      ) : totalAppointments === 0 || appointments?.length === 0 ? (
        <Box
          height={"50px"}
          padding={"40px"}
          textAlign={"center"}
          fontWeight={600}
          color={"red"}
        >
          No Upcoming Appointments yet
        </Box>
      ) : (
        <Table
          tableReference="appointment"
          columns={colsToUse[reference]}
          data={rows}
          displayActions={displayActions}
          renderCell={(cellData, row, column) => {
            const isActionsColumn = column === "Actions";
            return (
              <Box
                onClick={(event) => {
                  if (!isActionsColumn) {
                    navigate(
                      `/patients/${id ?? row["Patient ID"]}/appointments/${
                        row["Appointment ID"]
                      }`
                    );
                  }
                }}
                cursor={isActionsColumn ? "default" : "pointer"}
                display="flex"
                alignItems="center"
                justifyContent="left"
                padding="8px"
              >
                {isActionsColumn
                  ? renderActionsCell(cellData, column, row)
                  : column === "Appointment Type"
                  ? renderAppointmentTypeCell(cellData)
                  : cellData}
              </Box>
            );
          }}
        />
      )}
    </Box>
  );
};

export default AppointmentTable;
